import { getDateInputFormat, systemNow } from '.'
import { getDateFromDatetime } from './dates'
import * as service from '../_services/lender.service'

/**
 * Builds an in memory invoice representation.
 * The transiend_id is mandatory and should unique identify an invoice even before the invoice be created in the backend.
 * After being persisted, the "backend_id" should be filled.
 * The 'persisted' field indicates if the invoice is synchronized with the backend.
 */
export const buildTransientInvoice = (transient_id, business_id, debtor_id) => {
  const defaultDebtorDueDate = systemNow()
  defaultDebtorDueDate.setDate(defaultDebtorDueDate.getDate() + 30)
  const invoice = {
    id: transient_id,
    backend_id: null,
    persisted: false,
    business_id,
    po_number: null,
    invoice_number: null,
    invoice_date: getDateInputFormat(systemNow()),
    debtor_due_date: getDateInputFormat(defaultDebtorDueDate),
    payment_terms: 90,
    face_value: null,
    debtor_id,
    fund: true,
  }
  invoice.errors = validateInvoice(invoice)
  return invoice
}

/**
 * Sync the list of transient invoices with the backend.
 * Creates new invoices when backend_id=null. Updates invoices with the persisted=false.
 */
export const syncInvoices = async (transientInvoices, invoiceScheduleId) => {
  const requestPromises = transientInvoices.map((transientInvoice) => {
    if (!transientInvoice.backend_id) {
      const createRequest = buildCreateRequest(transientInvoice, invoiceScheduleId)
      return service.createInvoiceForSchedule(createRequest)
    } else if (!transientInvoice.persisted) {
      const updateRequest = updateRequestBody(transientInvoice)
      return service.updateInvoice(transientInvoice.backend_id, updateRequest)
    }
  })

  return Promise.all(requestPromises)
}

const validateInvoice = (invoice) => {
  const errors = {}
  if (!invoice.debtor_id) {
    errors.debtor_id = "Debtor can't be blank"
  }
  if (!invoice.face_value || invoice.face_value <= 0) {
    errors.face_value = 'Invoice Amount must be greater than 0'
  }
  if (!invoice.invoice_number) {
    errors.invoice_number = "Invoice number can't be blank"
  }
  const today = getDateInputFormat(systemNow())
  if (invoice.invoice_date && new Date(invoice.invoice_date) > new Date(today)) {
    errors.invoice_date = 'Invoice date is set in the future'
  }
  if (!invoice.payment_terms) {
    errors.payment_terms = "Terms can't be blank"
  }
  return errors
}

export const updateTransientInvoice = (invoice, data) => {
  const updatedInvoice = { ...invoice, ...data }
  updatedInvoice.persisted = false
  updatedInvoice.errors = validateInvoice(updatedInvoice)
  return updatedInvoice
}

export const invoiceToTransient = (invoice, transient_id) => {
  invoice.persisted = true
  invoice.backend_id = invoice.id
  invoice.id = transient_id
  if (invoice.invoice_date) {
    invoice.invoice_date = getDateFromDatetime(invoice.invoice_date)
  }
  if (invoice.debtor_due_date) {
    invoice.debtor_due_date = getDateFromDatetime(invoice.debtor_due_date)
  }

  return invoice
}

export const invoiceResponseToTransient = (persistedInvoice, transient_id) => {
  return {
    ...persistedInvoice,
    backend_id: persistedInvoice.id,
    id: transient_id,
    persisted: true,
    fund: !persistedInvoice.non_funded,
  }
}

const buildCreateRequest = (transientInvoice, schedule_id) => {
  const validCreateKeys = [
    'business_id',
    'invoice_date',
    'debtor_due_date',
    'payment_terms',
    'face_value',
    'debtor_id',
    'invoice_number',
    'po_number',
  ]
  const invoice_data = filterKeys(transientInvoice, validCreateKeys)
  invoice_data.non_funded = !transientInvoice.fund
  invoice_data.non_factored = !transientInvoice.fund
  return {
    invoice_schedule_id: schedule_id,
    invoice: invoice_data,
  }
}

export const updateRequestBody = (transientInvoice) => {
  const validUpdateKeys = ['debtor_id', 'invoice_number', 'po_number', 'invoice_date', 'payment_terms', 'face_value']
  const invoice_data = filterKeys(transientInvoice, validUpdateKeys)
  if (transientInvoice.fund === true || transientInvoice.fund === false) {
    // exclude null and undefined
    invoice_data.non_funded = !transientInvoice.fund
    invoice_data.non_factored = !transientInvoice.fund
  }
  invoice_data.face_value = invoice_data.face_value || 0
  return invoice_data
}

const filterKeys = (transientInvoice, validKeys) => {
  return Object.fromEntries(Object.entries(transientInvoice).filter(([key, _value]) => validKeys.includes(key)))
}

export const replaceInvoice = (invoices, transientInvoice) => {
  return invoices.map((i) => {
    if (i.id == transientInvoice.id) {
      return transientInvoice
    } else {
      return i
    }
  })
}

export const getInvoicesErrors = (invoices) => {
  let errors = {}
  invoices.forEach((i) => {
    errors = { ...errors, ...i.errors }
  })
  return errors
}

export const findTransientInvoice = (transientInvoices, id) => {
  return transientInvoices.find((i) => {
    return i.id == id
  })
}

export const allPersisted = (invoices) => {
  const not_persisted = invoices.filter((i) => {
    return i.persisted == false
  })
  return not_persisted.length == 0
}

export const thereAreInvoiceErrors = (invoices) => {
  return Object.keys(getInvoicesErrors(invoices)).length !== 0
}
